import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MY_BETS_DATE_PRESETS,
  myBetsDateFrom,
  type MyBetsDatePresetDays,
} from '@/domain/tickets';
import {
  TRANSACTIONS_DEFAULT_DATE_DAYS,
  TRANSACTIONS_PAGE_SIZE,
  filterTransactionsByCategory,
  type MachineBalanceRow,
  type TransactionsCategory,
  type TransactionsListRequest,
  type TransactionsListResponse,
} from '@/domain/transactions';
import { getUserSettings } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import { queryKeys } from './query-keys';

/** Wallet ledger list (`sb/api/player/profile/transactions`). */
export function listTransactions(
  request: TransactionsListRequest,
  signal?: AbortSignal,
): Promise<TransactionsListResponse> {
  return api.post<TransactionsListResponse>(
    endpoints.player.transactions.host,
    endpoints.player.transactions.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

const AUTO_FETCH_MAX_PAGES = 5;

export interface UseTransactionsResult {
  category: TransactionsCategory;
  setCategory: (category: TransactionsCategory) => void;
  dateDays: MyBetsDatePresetDays;
  setDateDays: (days: MyBetsDatePresetDays) => void;
  datePresets: typeof MY_BETS_DATE_PRESETS;
  rows: MachineBalanceRow[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  listError: Error | null;
  currency: string;
  sentinelRef: (node: HTMLDivElement | null) => void;
}

/**
 * Transactions filter state + infinite ledger list.
 * Category (All/Sports/Casino) is applied client-side; date preset → DateFrom like My Bets.
 */
export function useTransactions(enabled = true): UseTransactionsResult {
  const [category, setCategory] = useState<TransactionsCategory>('all');
  const [dateDays, setDateDays] = useState<MyBetsDatePresetDays>(
    TRANSACTIONS_DEFAULT_DATE_DAYS,
  );

  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';
  const currency = (user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode ?? '').trim();

  const dateFrom = useMemo(() => myBetsDateFrom(dateDays), [dateDays]);

  const listFilterKey = useMemo(
    () => ({
      machineGuid,
      dateFrom,
    }),
    [dateFrom, machineGuid],
  );

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.player.transactions(listFilterKey),
    enabled: enabled && Boolean(machineGuid),
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const response = await listTransactions(
        {
          TableFilters: {
            MachineGuid: machineGuid,
            DateFrom: dateFrom,
            DateTo: null,
          },
          Paging: {
            PageNumber: pageParam,
            NumberOfResultsToDisplay: TRANSACTIONS_PAGE_SIZE,
          },
          Sorting: { SortBy: 'Date', Direction: 'desc' },
        },
        signal,
      );
      return response;
    },
    getNextPageParam: (lastPage) => {
      const page = lastPage.Filter?.PageNumber ?? 1;
      const pages = lastPage.Filter?.NumberOfPages ?? 1;
      return page < pages ? page + 1 : undefined;
    },
  });

  const rawRows = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.Data ?? []) ?? [],
    [listQuery.data],
  );

  const rows = useMemo(
    () => filterTransactionsByCategory(rawRows, category),
    [category, rawRows],
  );

  const fetchNextPage = listQuery.fetchNextPage;
  const hasNextPage = Boolean(listQuery.hasNextPage);
  const isFetchingNextPage = listQuery.isFetchingNextPage;

  // When a category chip hides most of a page, keep loading until we have rows or run out.
  useEffect(() => {
    if (!enabled || category === 'all' || !hasNextPage || isFetchingNextPage) return;
    if (listQuery.isLoading || listQuery.isFetching) return;
    const pagesLoaded = listQuery.data?.pages.length ?? 0;
    if (pagesLoaded === 0 || pagesLoaded >= AUTO_FETCH_MAX_PAGES) return;
    if (rows.length >= 10) return;
    void fetchNextPage();
  }, [
    category,
    enabled,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    listQuery.data?.pages.length,
    listQuery.isFetching,
    listQuery.isLoading,
    rows.length,
  ]);

  const [sentinelNode, setSentinelNode] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinelNode(node);
  }, []);

  useEffect(() => {
    if (!sentinelNode || !enabled || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [enabled, fetchNextPage, hasNextPage, isFetchingNextPage, sentinelNode]);

  return {
    category,
    setCategory,
    dateDays,
    setDateDays,
    datePresets: MY_BETS_DATE_PRESETS,
    rows,
    isLoading: listQuery.isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: () => {
      void fetchNextPage();
    },
    listError: listQuery.error instanceof Error ? listQuery.error : null,
    currency,
    sentinelRef,
  };
}
